import { useState, useEffect, useRef, useCallback } from 'react';
import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';
import { applyInferredScheduleOnComplete } from '../services/inferredScheduleService';
import { persistLastUsedCountFormats } from '../services/countFormatMemoryService';

const DB_NAME = 'CountertopOfflineDB';
const DB_VERSION = 1;
const STORE_CHANGES = 'pendingChanges';
const STORE_DRAFTS = 'sessionDrafts';
const PING_URL = '/api/mobile/ping';
const SUPABASE_REST_PROBE_URL = `${SUPABASE_URL}/rest/v1/`;
const BATCH_SYNC_URL = '/api/mobile/sync-count-batch';
const PING_INTERVAL_MS = 30_000;
const SYNC_RETRY_MS = 6_000;
// Synced IndexedDB rows older than this are purged so device storage stays bounded.
const CLEANUP_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// ── IndexedDB helpers ────────────────────────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_CHANGES)) {
        const store = db.createObjectStore(STORE_CHANGES, { keyPath: 'queueKey' });
        store.createIndex('bySession', 'sessionId', { unique: false });
        store.createIndex('byStatus', 'syncStatus', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        db.createObjectStore(STORE_DRAFTS, { keyPath: 'draftKey' });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

function idbPut(db, storeName, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).put(value);
    req.onsuccess = () => resolve();
    req.onerror = (e) => reject(e.target.error);
  });
}

function idbGet(db, storeName, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

function idbGetAll(db, storeName) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = (e) => resolve(e.target.result || []);
    req.onerror = (e) => reject(e.target.error);
  });
}

function idbDelete(db, storeName, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = (e) => reject(e.target.error);
  });
}

function parseTsMs(s) {
  if (!s) return 0;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : 0;
}

// Purge synced offline data older than CLEANUP_MAX_AGE_MS so a device used
// daily for counts doesn't accumulate IndexedDB rows indefinitely.
//   - pendingChanges with syncStatus 'synced' and a stale localUpdatedAt
//   - sessionDrafts completion records ('completion' type) that are synced
//     and whose completedAt is stale
//   - sessionDrafts payload records ('payload' type) whose savedAt is stale
//     AND have no remaining queued changes for that session (so we never
//     wipe a draft a user might still need to reopen offline)
async function purgeOldSyncedData(db) {
  if (!db) return 0;
  const cutoff = Date.now() - CLEANUP_MAX_AGE_MS;
  let deleted = 0;
  try {
    const changes = await idbGetAll(db, STORE_CHANGES);
    const oldChangeKeys = changes
      .filter((r) => r && r.syncStatus === 'synced' && parseTsMs(r.localUpdatedAt) > 0 && parseTsMs(r.localUpdatedAt) < cutoff)
      .map((r) => r.queueKey)
      .filter(Boolean);
    if (oldChangeKeys.length > 0) {
      await Promise.all(oldChangeKeys.map((k) => idbDelete(db, STORE_CHANGES, k).catch(() => {})));
      deleted += oldChangeKeys.length;
    }

    const drafts = await idbGetAll(db, STORE_DRAFTS);
    const oldCompletionKeys = drafts
      .filter((d) => d && d.type === 'completion' && d.syncStatus === 'synced' && parseTsMs(d.completedAt) > 0 && parseTsMs(d.completedAt) < cutoff)
      .map((d) => d.draftKey)
      .filter(Boolean);

    // Recompute remaining changes after the deletes above so we know which
    // sessions still have queued work and must keep their payload draft.
    const remainingChanges = await idbGetAll(db, STORE_CHANGES);
    const sessionsWithChanges = new Set(remainingChanges.map((r) => r && r.sessionId).filter(Boolean));
    const oldPayloadKeys = drafts
      .filter((d) => d && d.type === 'payload' && parseTsMs(d.savedAt) > 0 && parseTsMs(d.savedAt) < cutoff && !sessionsWithChanges.has(d.sessionId))
      .map((d) => d.draftKey)
      .filter(Boolean);

    const oldDraftKeys = [...oldCompletionKeys, ...oldPayloadKeys];
    if (oldDraftKeys.length > 0) {
      await Promise.all(oldDraftKeys.map((k) => idbDelete(db, STORE_DRAFTS, k).catch(() => {})));
      deleted += oldDraftKeys.length;
    }
  } catch (_) {
    // best-effort cleanup; never throw
  }
  return deleted;
}

function idbGetByIndex(db, storeName, indexName, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const idx = tx.objectStore(storeName).index(indexName);
    const req = idx.getAll(value);
    req.onsuccess = (e) => resolve(e.target.result || []);
    req.onerror = (e) => reject(e.target.error);
  });
}

// ── Connectivity ─────────────────────────────────────────────────────────────

// Diagnostics state shared by probePing and getProbeDiagnostics.
// Module-level so it persists across hook mounts.
let _lastProbeAt = null;
let _lastResult = null;
let _lastError = null;
// Timestamp (ms) of the last successful real batch sync call. When a real
// batch-sync or completion request succeeds it proves connectivity regardless
// of what the standalone ping probe says. If this happened within the last
// RECENT_SYNC_WINDOW_MS, the periodic ping check will not override isOnline
// to false — preventing the yellow offline icon from appearing while the
// queue is actively draining.
let _lastSuccessfulRealSyncMs = 0;
const RECENT_SYNC_WINDOW_MS = 60_000;

async function probePing() {
  // Do NOT short-circuit on navigator.onLine — iOS WKWebView (and some
  // Android WebView contexts) return stale or incorrect values for this
  // property until a real network event fires. Always attempt an actual
  // HTTP probe so the iPhone simulator WebView doesn't get stuck offline.
  let primaryError = null;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    let resp;
    try {
      resp = await fetch(PING_URL, { signal: ctrl.signal, cache: 'no-store' });
    } finally {
      clearTimeout(timer);
    }
    if (resp.ok) {
      _lastProbeAt = new Date().toISOString();
      _lastResult = true;
      _lastError = null;
      return true;
    }
    // HTTP error (4xx/5xx) — server reachable but something is wrong.
    // Treat as offline; don't fall through to the Supabase fallback.
    primaryError = `HTTP ${resp.status}`;
    console.warn(`[OfflineSync] probe failed: ${PING_URL} returned ${resp.status}`);
    _lastProbeAt = new Date().toISOString();
    _lastResult = false;
    _lastError = primaryError;
    return false;
  } catch (err) {
    // Network error, abort, DNS failure — try Supabase REST fallback.
    primaryError = err?.message || 'network error';
    console.warn(`[OfflineSync] probe failed: ${PING_URL} — ${primaryError}. Trying Supabase REST fallback.`);
  }

  // Supabase REST fallback: a cheap HEAD against the REST root. No auth
  // needed — returns 200 for any reachable Supabase project.
  try {
    const ctrl2 = new AbortController();
    const timer2 = setTimeout(() => ctrl2.abort(), 5000);
    let resp2;
    try {
      resp2 = await fetch(SUPABASE_REST_PROBE_URL, {
        method: 'HEAD',
        signal: ctrl2.signal,
        cache: 'no-store',
        headers: { apikey: SUPABASE_KEY },
      });
    } finally {
      clearTimeout(timer2);
    }
    const online = resp2.ok || resp2.status === 200;
    _lastProbeAt = new Date().toISOString();
    _lastResult = online;
    _lastError = online ? null : `Supabase fallback HTTP ${resp2.status}`;
    if (!online) {
      console.warn(`[OfflineSync] probe failed: Supabase fallback returned ${resp2.status}`);
    }
    return online;
  } catch (fallbackErr) {
    const msg = fallbackErr?.message || 'network error';
    console.warn(`[OfflineSync] probe failed: Supabase fallback — ${msg}. Device appears offline.`);
    _lastProbeAt = new Date().toISOString();
    _lastResult = false;
    _lastError = `primary: ${primaryError}; fallback: ${msg}`;
    return false;
  }
}

// ── Device-draft ID persistence ──────────────────────────────────────────────

function getOrCreateDeviceDraftId(sessionId) {
  const key = `ctop_device_draft_${sessionId}`;
  let id = null;
  try { id = localStorage.getItem(key); } catch (_) {}
  if (!id) {
    id = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    try { localStorage.setItem(key, id); } catch (_) {}
  }
  return id;
}

// ── Batch sync helper ─────────────────────────────────────────────────────────
// Calls the server's idempotent batch endpoint which proxies to Supabase with
// the snapshot-divergence guard and returns per-row status + confirmation code.

async function callSyncBatch(supabase, sessionId, snapshotId, snapshotTimestamp, deviceDraftId, rows) {
  if (!rows || rows.length === 0) return { synced: [], sync_problem: [], retryable_failure: [], batch_confirmation_code: null };
  let userJwt = null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    userJwt = session?.access_token || null;
  } catch (_) {}

  if (!userJwt) {
    return { synced: [], sync_problem: [], retryable_failure: rows.map(r => r.item_id), batch_confirmation_code: null };
  }

  const resp = await fetch(BATCH_SYNC_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userJwt}`,
    },
    body: JSON.stringify({
      session_id: sessionId,
      snapshot_id: snapshotId || 'unknown',
      snapshot_timestamp: snapshotTimestamp || new Date(0).toISOString(),
      device_draft_id: deviceDraftId || 'unknown',
      rows,
    }),
    signal: AbortSignal.timeout(15_000),
  });

  if (!resp.ok) {
    const errText = await resp.text().catch(() => '');
    throw new Error(`Sync batch ${resp.status}: ${errText}`);
  }
  return resp.json();
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useOfflineCountSync
 *
 * Provides local-first count-item persistence for the mobile count tool.
 * Only active when `enabled` is true (mobileTool prop).
 *
 * @param {object} opts
 * @param {boolean}  opts.enabled           - Activate the hook (only when mobileTool=true)
 * @param {string}   opts.sessionId         - Active count session ID
 * @param {string}   opts.snapshotId        - Snapshot fingerprint of the loaded item list
 * @param {string}   opts.snapshotTimestamp - ISO timestamp when the session items were loaded
 * @param {object}   opts.supabase          - Supabase client instance
 * @param {function} opts.onSyncToast       - Called with a toast message when all pending synced
 */
export function useOfflineCountSync({ enabled, sessionId, snapshotId, snapshotTimestamp, supabase, onSyncToast, onSessionCompleted }) {
  const dbRef = useRef(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // itemId → 'pending' | 'syncing' | 'synced' | 'failed'
  const [itemSyncStatuses, setItemSyncStatuses] = useState({});
  const [pendingOtherSession, setPendingOtherSession] = useState(null);
  const isSyncingRef = useRef(false);
  const deviceDraftId = sessionId ? getOrCreateDeviceDraftId(sessionId) : null;
  const toastFiredRef = useRef(false);

  // ── DB init ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    openDB()
      .then((db) => {
        if (cancelled) { db.close(); return; }
        dbRef.current = db;
        // Purge synced rows older than the cleanup window so device storage
        // doesn't grow unbounded across daily counts. Best-effort, async.
        purgeOldSyncedData(db).catch(() => {});
        if (sessionId) {
          idbGetByIndex(db, STORE_CHANGES, 'bySession', sessionId).then(async (rows) => {
            if (cancelled) return;
            const statuses = {};
            const staleWrites = [];
            rows.forEach((r) => {
              // Any row stuck in 'syncing' means the previous in-flight batch was
              // interrupted (app closed / killed mid-request). Treat it as pending
              // so the sync runner picks it up again on this session.
              const effectiveStatus = r.syncStatus === 'syncing' ? 'pending' : r.syncStatus;
              statuses[r.itemId] = effectiveStatus;
              if (r.syncStatus === 'syncing') {
                staleWrites.push(idbPut(db, STORE_CHANGES, { ...r, syncStatus: 'pending' }));
              }
            });
            setItemSyncStatuses(statuses);
            if (staleWrites.length > 0) await Promise.all(staleWrites).catch(() => {});
          }).catch(() => {});

          // Normalize any stale 'syncing' completion records across ALL sessions
          // (covers interrupted completion requests on reopen, for both the current
          // session and any other sessions that were mid-completion when killed).
          idbGetAll(db, STORE_DRAFTS).then(async (allDrafts) => {
            if (cancelled) return;
            const staleComps = (allDrafts || []).filter(
              (d) => d.draftKey && d.draftKey.endsWith('__completion') && d.syncStatus === 'syncing'
            );
            await Promise.all(
              staleComps.map((d) => idbPut(db, STORE_DRAFTS, { ...d, syncStatus: 'pending' }))
            ).catch(() => {});
          }).catch(() => {});
        }
        checkOtherSessionPending(db, sessionId);
      })
      .catch((err) => console.warn('[OfflineSync] IndexedDB unavailable:', err?.message));
    return () => {
      cancelled = true;
      if (dbRef.current) { try { dbRef.current.close(); } catch (_) {} dbRef.current = null; }
    };
  }, [enabled, sessionId]);

  async function checkOtherSessionPending(db, currentSid) {
    try {
      const all = await idbGetAll(db, STORE_CHANGES);
      // Include 'syncing' here: those rows are stale from an interrupted previous
      // sync run. Normalize them to 'pending' so the sync runner will retry them.
      const otherUnsynced = all.filter(
        (r) => r.sessionId !== currentSid &&
               (r.syncStatus === 'pending' || r.syncStatus === 'syncing' || r.syncStatus === 'failed')
      );
      if (otherUnsynced.length === 0) return;
      const otherSid = otherUnsynced[0].sessionId;
      // Normalize stale 'syncing' rows for other sessions so syncSession drains them
      const staleOther = otherUnsynced.filter((r) => r.syncStatus === 'syncing');
      await Promise.all(
        staleOther.map((r) => idbPut(db, STORE_CHANGES, { ...r, syncStatus: 'pending' }))
      ).catch(() => {});
      setPendingOtherSession({ sessionId: otherSid, count: otherUnsynced.length });
      const online = await probePing();
      if (online) syncSession(db, otherSid);
    } catch (_) {}
  }

  // ── Connectivity monitoring ────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const check = async () => {
      if (cancelled) return;
      const online = await probePing();
      if (!cancelled) {
        // A recent successful real batch-sync proves connectivity. Don't let a
        // transient ping failure flip the indicator offline while the queue is
        // actively draining — the real save endpoint is clearly reachable.
        const recentSync = Date.now() - _lastSuccessfulRealSyncMs < RECENT_SYNC_WINDOW_MS;
        if (!online && recentSync) return;
        setIsOnline(online);
      }
    };

    check();
    const pingTimer = setInterval(check, PING_INTERVAL_MS);

    const onOnline = () => check();
    const onOffline = () => { if (!cancelled) setIsOnline(false); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      cancelled = true;
      clearInterval(pingTimer);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [enabled]);

  // ── Sync runner — drains pending queue when online ─────────────────────────
  useEffect(() => {
    if (!enabled || !sessionId) return;
    if (!isOnline) return;

    const run = () => {
      if (dbRef.current) syncSession(dbRef.current, sessionId);
    };
    run();
    const t = setInterval(run, SYNC_RETRY_MS);
    return () => clearInterval(t);
  }, [enabled, isOnline, sessionId, supabase]);

  // ── syncSession — batch all pending rows through the idempotent endpoint ───
  // Only retries 'pending' and 'retryable_failure' rows. Rows with 'failed'
  // (sync_problem/snapshot-diverged) stay failed until explicit user retry.
  async function syncSession(db, targetSessionId) {
    if (isSyncingRef.current) return;
    if (!supabase) return;
    isSyncingRef.current = true;
    let syncedCount = 0;

    try {
      const rows = await idbGetByIndex(db, STORE_CHANGES, 'bySession', targetSessionId);
      const pending = rows.filter((r) => r.syncStatus === 'pending');
      if (pending.length === 0) {
        await syncSessionCompletionIfQueued(db, targetSessionId, 0);
        return;
      }

      // Mark all as syncing in IDB and UI
      for (const record of pending) {
        await idbPut(db, STORE_CHANGES, { ...record, syncStatus: 'syncing' });
        if (targetSessionId === sessionId) {
          setItemSyncStatuses((prev) => ({ ...prev, [record.itemId]: 'syncing' }));
        }
      }

      // Build batch rows using each record's own snapshotId/snapshotTimestamp
      // (preserves the context from when each change was originally enqueued)
      const batchRows = pending.map((r) => ({ item_id: r.itemId, patch: r.patch }));

      // Use the snapshotTimestamp from the first pending record (all same session)
      const snapTs = pending[0]?.snapshotTimestamp || snapshotTimestamp || new Date(0).toISOString();
      const snapId = pending[0]?.snapshotId || snapshotId || 'unknown';
      const draftId = pending[0]?.deviceDraftId || deviceDraftId || 'unknown';

      let batchResult;
      try {
        batchResult = await callSyncBatch(supabase, targetSessionId, snapId, snapTs, draftId, batchRows);
        // A successful real batch call confirms connectivity — mark now so the
        // periodic ping check doesn't flip the header indicator offline while
        // the queue is actively draining.
        _lastSuccessfulRealSyncMs = Date.now();
        setIsOnline(true);
      } catch (batchErr) {
        // Batch call itself failed (network down, server error) — revert to pending
        for (const record of pending) {
          await idbPut(db, STORE_CHANGES, { ...record, syncStatus: 'pending', lastError: batchErr?.message });
          if (targetSessionId === sessionId) {
            setItemSyncStatuses((prev) => ({ ...prev, [record.itemId]: 'pending' }));
          }
        }
        return;
      }

      const syncedSet = new Set(batchResult.synced || []);
      const problemSet = new Set(batchResult.sync_problem || []);

      for (const record of pending) {
        let newStatus;
        if (syncedSet.has(record.itemId)) {
          newStatus = 'synced';
          syncedCount++;
        } else if (problemSet.has(record.itemId)) {
          newStatus = 'failed';
        } else {
          // In retryable_failure — keep as pending for next attempt
          newStatus = 'pending';
        }
        await idbPut(db, STORE_CHANGES, { ...record, syncStatus: newStatus, lastError: newStatus === 'failed' ? 'sync_problem: snapshot diverged or auth error' : null });
        if (targetSessionId === sessionId) {
          setItemSyncStatuses((prev) => ({ ...prev, [record.itemId]: newStatus }));
        }
      }

      if (syncedCount > 0 && targetSessionId !== sessionId) {
        setPendingOtherSession(null);
      }

      await syncSessionCompletionIfQueued(db, targetSessionId, syncedCount, batchResult.batch_confirmation_code);

      // Lightweight cleanup at the end of each successful sync batch so that
      // freshly-synced rows that have aged out are reclaimed promptly instead
      // of waiting for the next app reopen.
      purgeOldSyncedData(db).catch(() => {});

    } catch (err) {
      console.warn('[OfflineSync] Sync run error:', err?.message);
    } finally {
      isSyncingRef.current = false;
    }
  }

  // After all items sync, push session completion and fire toast
  async function syncSessionCompletionIfQueued(db, targetSessionId, syncedItemCount, confirmationCode) {
    if (!supabase) return;
    const completionKey = `${targetSessionId}__completion`;
    let completionRecord;
    try { completionRecord = await idbGet(db, STORE_DRAFTS, completionKey); } catch (_) { return; }
    if (!completionRecord || completionRecord.syncStatus !== 'pending') {
      // No completion queued — still fire toast if items synced for current session
      if (targetSessionId === sessionId && !toastFiredRef.current && onSyncToast && syncedItemCount > 0) {
        const itemRows = await idbGetByIndex(db, STORE_CHANGES, 'bySession', targetSessionId);
        const stillPending = itemRows.filter((r) => r.syncStatus === 'pending' || r.syncStatus === 'syncing' || r.syncStatus === 'failed');
        if (stillPending.length === 0) {
          toastFiredRef.current = true;
          const codeStr = confirmationCode ? ` Confirmation ${confirmationCode}.` : '';
          onSyncToast(`Synced.${codeStr}`);
        }
      }
      return;
    }

    // Block completion if any item rows are unresolved (pending, syncing, or failed/sync_problem)
    const itemRows = await idbGetByIndex(db, STORE_CHANGES, 'bySession', targetSessionId).catch(() => []);
    const stillUnresolved = itemRows.filter(
      (r) => r.syncStatus === 'pending' || r.syncStatus === 'syncing' || r.syncStatus === 'failed'
    );
    if (stillUnresolved.length > 0) return;

    try {
      await idbPut(db, STORE_DRAFTS, { ...completionRecord, syncStatus: 'syncing' });
      const { error } = await supabase
        .from('count_sessions')
        .update({ status: 'completed', completed_at: completionRecord.completedAt })
        .eq('id', targetSessionId);
      if (error) throw error;

      await idbPut(db, STORE_DRAFTS, { ...completionRecord, syncStatus: 'synced' });

      // Inferred scheduling runs off the moment the count was FINISHED, not
      // the moment the queue drained — a Tuesday count that syncs on Thursday
      // still means "Tuesdays". completionRecord.completedAt is that moment,
      // and it is the same value written to completed_at above.
      await applyInferredScheduleOnComplete(supabase, targetSessionId, {
        completedAt: completionRecord.completedAt,
      });
      // Remember what package each location was counted in — same moment
      // rule: the count's own finish time, not the sync time.
      await persistLastUsedCountFormats(supabase, targetSessionId, {
        completedAt: completionRecord.completedAt,
      });

      // Fire draft creation callback after the session is marked complete in
      // Supabase. The edge function is idempotent so a double-fire is harmless.
      // We forward the persisted `skipDraftCreation` flag so that sessions the
      // user explicitly finalized as "No order" do not get an auto-draft when
      // the queued completion eventually syncs.
      let draftNote = '';
      if (onSessionCompleted) {
        try {
          const draftData = await onSessionCompleted(targetSessionId, {
            skipDraftCreation: !!completionRecord.skipDraftCreation,
          });
          if (draftData?.drafts?.length > 0) {
            const n = draftData.drafts.length;
            draftNote = ` ${n} draft order${n > 1 ? 's' : ''} created in Ordering.`;
          }
        } catch (_) {}
      }

      if (targetSessionId === sessionId && !toastFiredRef.current && onSyncToast) {
        toastFiredRef.current = true;
        const codeStr = confirmationCode ? ` Confirmation ${confirmationCode}.` : '';
        onSyncToast(`Synced.${codeStr}${draftNote}`);
      }
    } catch (err) {
      await idbPut(db, STORE_DRAFTS, { ...completionRecord, syncStatus: 'pending', lastError: err?.message }).catch(() => {});
    }
  }

  // ── queueChange — write local-first, attempt batch sync immediately ─────────
  const queueChange = useCallback(
    async (itemId, patch) => {
      if (!enabled || !sessionId) return 'untracked';

      const queueKey = `${sessionId}_${itemId}`;
      const record = {
        queueKey,
        itemId,
        sessionId,
        snapshotId: snapshotId || 'unknown',
        snapshotTimestamp: snapshotTimestamp || new Date(0).toISOString(),
        deviceDraftId: deviceDraftId || 'unknown',
        patch,
        localUpdatedAt: new Date().toISOString(),
        syncStatus: 'pending',
        lastError: null,
      };

      // 1. Write to IndexedDB immediately (local-first, status = pending)
      if (dbRef.current) {
        try { await idbPut(dbRef.current, STORE_CHANGES, record); } catch (_) {}
      }
      setItemSyncStatuses((prev) => ({ ...prev, [itemId]: 'pending' }));

      // 2. Attempt immediate batch sync if online
      if (!isOnline || !supabase) return 'pending';

      try {
        setItemSyncStatuses((prev) => ({ ...prev, [itemId]: 'syncing' }));
        const batchResult = await callSyncBatch(
          supabase, sessionId, snapshotId, snapshotTimestamp, deviceDraftId,
          [{ item_id: itemId, patch }]
        );
        // Successful real sync call — mark as authoritative online.
        _lastSuccessfulRealSyncMs = Date.now();
        setIsOnline(true);

        const isSynced = (batchResult.synced || []).includes(itemId);
        const isProblem = (batchResult.sync_problem || []).includes(itemId);

        let finalStatus;
        if (isSynced) {
          finalStatus = 'synced';
        } else if (isProblem) {
          finalStatus = 'failed';
        } else {
          finalStatus = 'pending';
        }

        const updated = { ...record, syncStatus: finalStatus, lastError: isProblem ? 'sync_problem: snapshot diverged' : null };
        if (dbRef.current) {
          try { await idbPut(dbRef.current, STORE_CHANGES, updated); } catch (_) {}
        }
        setItemSyncStatuses((prev) => ({ ...prev, [itemId]: finalStatus }));
        return finalStatus;
      } catch (err) {
        const pending2 = { ...record, syncStatus: 'pending', lastError: err?.message };
        if (dbRef.current) {
          try { await idbPut(dbRef.current, STORE_CHANGES, pending2); } catch (_) {}
        }
        setItemSyncStatuses((prev) => ({ ...prev, [itemId]: 'pending' }));
        return 'pending';
      }
    },
    [enabled, sessionId, snapshotId, snapshotTimestamp, deviceDraftId, isOnline, supabase]
  );

  // ── saveSessionDraft — persist item list + snapshot context for offline reopen ─
  // Snapshot context (snapshotId, snapshotTimestamp, deviceDraftId) is stored so
  // that queueChange uses the correct timestamp after an offline reopen rather
  // than the epoch fallback, which would make every sync appear as a divergence.
  const saveSessionDraft = useCallback(
    async (sid, items, snapId, snapTs) => {
      if (!enabled || !dbRef.current || !sid) return;
      try {
        await idbPut(dbRef.current, STORE_DRAFTS, {
          draftKey: sid,
          sessionId: sid,
          items,
          snapshotId: snapId || null,
          snapshotTimestamp: snapTs || null,
          deviceDraftId: deviceDraftId || null,
          savedAt: new Date().toISOString(),
          type: 'payload',
        });
      } catch (err) {
        console.warn('[OfflineSync] saveSessionDraft failed:', err?.message);
      }
    },
    [enabled, deviceDraftId]
  );

  // ── loadSessionDraft — restore item list + snapshot context on offline reopen ──
  // Applies any locally queued changes on top of the saved draft so the user
  // sees their latest unsynced edits. Returns { items, snapshotId, snapshotTimestamp }
  // so callers can restore full snapshot context into React state.
  const loadSessionDraft = useCallback(
    async (sid) => {
      if (!dbRef.current || !sid) return null;
      try {
        const rec = await idbGet(dbRef.current, STORE_DRAFTS, sid);
        if (!rec?.items || rec.items.length === 0) return null;

        // Apply pending changes (including failed) on top of the saved draft
        const changes = await idbGetByIndex(dbRef.current, STORE_CHANGES, 'bySession', sid).catch(() => []);
        const changeMap = {};
        changes.forEach((c) => { changeMap[c.itemId] = c.patch; });

        const reconstituted = rec.items.map((item) => {
          const patch = changeMap[item.id];
          if (!patch) return item;
          // Direct order mode patches carry the order amount in
          // manual_order_quantity (never actual_quantity), so the input buffer
          // (current_count) is rebuilt from that instead of on-hand.
          const nextCurrentCount = item.direct_order_mode
            ? (patch.manual_order_quantity != null ? String(patch.manual_order_quantity) : item.current_count)
            : (patch.actual_quantity ?? item.current_count);
          return {
            ...item,
            current_count: nextCurrentCount,
            actual_quantity: patch.actual_quantity ?? item.actual_quantity,
            is_counted: patch.is_counted ?? item.is_counted,
            counted_at: patch.counted_at ?? item.counted_at,
            calculated_order_quantity: patch.calculated_order_quantity ?? item.calculated_order_quantity,
            manual_order_quantity: patch.manual_order_quantity ?? item.manual_order_quantity,
          };
        });

        return {
          items: reconstituted,
          snapshotId: rec.snapshotId || null,
          snapshotTimestamp: rec.snapshotTimestamp || null,
          deviceDraftId: rec.deviceDraftId || null,
        };
      } catch {
        return null;
      }
    },
    []
  );

  // ── queueSessionCompletion — persist offline "Done" for later sync ───────────
  const queueSessionCompletion = useCallback(
    async (completionData) => {
      if (!enabled || !sessionId || !dbRef.current) return;
      const completionKey = `${sessionId}__completion`;
      try {
        await idbPut(dbRef.current, STORE_DRAFTS, {
          draftKey: completionKey,
          sessionId,
          type: 'completion',
          completedAt: completionData.completedAt || new Date().toISOString(),
          // Persist the user's "No order" intent so the background sync
          // runner does NOT auto-create a draft order when the queued
          // completion eventually syncs to Supabase. Defaults to false to
          // preserve the legacy behavior for all existing queued records.
          skipDraftCreation: !!completionData.skipDraftCreation,
          syncStatus: 'pending',
          lastError: null,
        });
      } catch (err) {
        console.warn('[OfflineSync] queueSessionCompletion failed:', err?.message);
      }
    },
    [enabled, sessionId]
  );

  // ── hasPendingChanges — async check against IDB (includes failed) ────────────
  const hasPendingChanges = useCallback(
    async (targetSid) => {
      if (!dbRef.current) return false;
      const sid = targetSid || sessionId;
      if (!sid) return false;
      try {
        const rows = await idbGetByIndex(dbRef.current, STORE_CHANGES, 'bySession', sid);
        return rows.some((r) => r.syncStatus === 'pending' || r.syncStatus === 'syncing' || r.syncStatus === 'failed');
      } catch {
        return false;
      }
    },
    [sessionId]
  );

  // ── hasPendingCompletion — was Done pressed offline and not yet synced? ──────
  // Returns true if a `__completion` draft exists for the session and is still
  // unsynced ('pending' / 'syncing' / 'failed'). Used to restore the
  // "Return to internet" Done-blocked modal when the app is reopened mid-flow.
  const hasPendingCompletion = useCallback(
    async (targetSid) => {
      if (!dbRef.current) return false;
      const sid = targetSid || sessionId;
      if (!sid) return false;
      try {
        const rec = await idbGet(dbRef.current, STORE_DRAFTS, `${sid}__completion`);
        if (!rec) return false;
        return rec.syncStatus === 'pending' || rec.syncStatus === 'syncing' || rec.syncStatus === 'failed';
      } catch {
        return false;
      }
    },
    [sessionId]
  );

  // ── getPendingCompletion — read the queued completion record verbatim ───────
  // Returns the persisted record (including skipDraftCreation intent) or null
  // when no pending completion exists. Used by retry / resume paths to honor
  // the user's "No order — just complete count" choice across app reloads.
  const getPendingCompletion = useCallback(
    async (targetSid) => {
      if (!dbRef.current) return null;
      const sid = targetSid || sessionId;
      if (!sid) return null;
      try {
        const rec = await idbGet(dbRef.current, STORE_DRAFTS, `${sid}__completion`);
        if (!rec) return null;
        const isPending = rec.syncStatus === 'pending' || rec.syncStatus === 'syncing' || rec.syncStatus === 'failed';
        return isPending ? rec : null;
      } catch {
        return null;
      }
    },
    [sessionId]
  );

  // ── clearPendingCompletion — drop the queued completion record ───────────────
  // Called once the count is finalized online (saveProgress completed) so the
  // sync runner doesn't redundantly re-push the completion and we don't show
  // the "Return to internet" modal on the next reopen.
  const clearPendingCompletion = useCallback(
    async (targetSid) => {
      if (!dbRef.current) return;
      const sid = targetSid || sessionId;
      if (!sid) return;
      try { await idbDelete(dbRef.current, STORE_DRAFTS, `${sid}__completion`); } catch (_) {}
    },
    [sessionId]
  );

  // ── discardSessionLocally — wipe local draft + pending changes for a session ─
  // Used by the "Abandon count" flow. Drops the saved draft, any pending
  // completion record, and every queued item change for this session from IDB.
  // This only affects local memory: rows already synced to Supabase remain on
  // the server. It also prevents the sync runner from later pushing the
  // abandoned local edits over the server's current state.
  const discardSessionLocally = useCallback(
    async (targetSid) => {
      if (!dbRef.current) return;
      const sid = targetSid || sessionId;
      if (!sid) return;
      try {
        try { await idbDelete(dbRef.current, STORE_DRAFTS, sid); } catch (_) {}
        try { await idbDelete(dbRef.current, STORE_DRAFTS, `${sid}__completion`); } catch (_) {}
        const rows = await idbGetByIndex(dbRef.current, STORE_CHANGES, 'bySession', sid).catch(() => []);
        await Promise.all(
          (rows || []).map((r) =>
            idbDelete(dbRef.current, STORE_CHANGES, r.queueKey).catch(() => {})
          )
        );
      } catch (err) {
        console.warn('[OfflineSync] discardSessionLocally failed:', err?.message);
      }
    },
    [sessionId]
  );

  // ── getSessionSyncStatus — derived from itemSyncStatuses ─────────────────────
  const getSessionSyncStatus = useCallback(() => {
    const values = Object.values(itemSyncStatuses);
    if (values.length === 0) return 'synced';
    if (values.some((s) => s === 'failed')) return 'failed';
    if (values.some((s) => s === 'syncing')) return 'syncing';
    if (values.some((s) => s === 'pending')) return 'pending';
    return 'synced';
  }, [itemSyncStatuses]);

  // ── triggerSync — manually kick off a sync attempt ────────────────────────────
  const triggerSync = useCallback(() => {
    if (!dbRef.current || !sessionId) return;
    syncSession(dbRef.current, sessionId);
  }, [sessionId, isOnline, supabase]);

  // ── probeNow — fresh connectivity check (used by "Try again" buttons) ────────
  // Bypasses the periodic poll cadence so the UI reflects current state
  // immediately after a user gesture. Also updates the hook's isOnline state.
  const probeNow = useCallback(async () => {
    const online = await probePing();
    setIsOnline(online);
    return online;
  }, []);

  // ── getProbeDiagnostics — returns last probe details for debug overlays ──────
  const getProbeDiagnostics = useCallback(() => ({
    lastProbeAt: _lastProbeAt,
    lastResult: _lastResult,
    lastError: _lastError,
    navigatorOnline: typeof navigator !== 'undefined' ? navigator.onLine : null,
  }), []);

  return {
    isOnline,
    itemSyncStatuses,
    queueChange,
    saveSessionDraft,
    loadSessionDraft,
    queueSessionCompletion,
    hasPendingChanges,
    hasPendingCompletion,
    getPendingCompletion,
    clearPendingCompletion,
    discardSessionLocally,
    getSessionSyncStatus,
    triggerSync,
    probeNow,
    getProbeDiagnostics,
    pendingOtherSession,
    deviceDraftId,
  };
}
